import { ChevronRight, ExternalLink, ArrowRight } from 'lucide-react';
import { TOOLS_DATA } from '../data/toolsData';
import { ARTICLES_DATA, ARTICLE_CATEGORY_ORDER } from '../data/articlesData';
import { RELATED_TOOLS, RELATED_ARTICLES } from '../data/relatedContent';

// 記事ページの末尾に共通で付くブロック。
// 「関連ツール」「あわせて読みたい」「この記事を書いた人」「一覧へ戻る」の4つ。
// 記事本体のファイルは触らず、App.js から article_ で始まるページに自動で差し込まれる。

const MAX_RELATED = 3;

// 関連記事を決める。relatedContent.js に登録があればそれを使い、
// 足りない分は同じカテゴリ内の並び順（記事一覧と同じ）で隣にある記事から補う。
function pickRelatedArticles(page) {
  const self = ARTICLES_DATA.find((a) => a.page === page);
  if (!self) return [];

  const picked = [];
  const add = (p) => {
    if (p === page || picked.includes(p)) return;
    if (!ARTICLES_DATA.some((a) => a.page === p)) return;
    picked.push(p);
  };

  (RELATED_ARTICLES[page] || []).forEach(add);

  if (picked.length < MAX_RELATED) {
    const order = ARTICLE_CATEGORY_ORDER[self.category] || [];
    const idx = order.indexOf(page);
    // 自分の次から順に、一周するまで見る
    for (let step = 1; step < order.length && picked.length < MAX_RELATED; step++) {
      add(order[(idx + step + order.length) % order.length]);
    }
  }

  return picked.slice(0, MAX_RELATED).map((p) => ARTICLES_DATA.find((a) => a.page === p));
}

function openTool(tool, setCurrentPage) {
  if (tool.externalUrl) {
    window.open(tool.externalUrl, '_blank', 'noopener,noreferrer');
  } else if (tool.page) {
    setCurrentPage(tool.page);
  }
}

export default function ArticleFooter({ page, setCurrentPage }) {
  const tools = (RELATED_TOOLS[page] || [])
    .map((id) => TOOLS_DATA.find((t) => t.id === id))
    .filter((t) => t && t.available);
  const articles = pickRelatedArticles(page);

  return (
    <div className="max-w-3xl mx-auto px-4 pb-12 no-print">
      <div className="border-t border-gray-200 pt-10 space-y-8">

        {/* 関連ツール */}
        {tools.length > 0 && (
          <section>
            <h2 className="text-base font-bold text-gray-800 border-l-4 border-green-400 pl-3 mb-3">この記事に関連するツール</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {tools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => openTool(tool, setCurrentPage)}
                  className="text-left bg-green-50 border border-green-200 rounded-xl p-4 hover:bg-green-100 transition-colors"
                >
                  <p className="font-bold text-green-900 text-sm mb-1">{tool.title}</p>
                  <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{tool.description}</p>
                  <p className="mt-2 flex items-center gap-1 text-green-800 text-xs font-medium">
                    使ってみる {tool.externalUrl ? <ExternalLink size={12} /> : <ChevronRight size={12} />}
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* あわせて読みたい */}
        {articles.length > 0 && (
          <section>
            <h2 className="text-base font-bold text-gray-800 border-l-4 border-green-400 pl-3 mb-3">あわせて読みたい</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {articles.map((article) => (
                <button
                  key={article.id}
                  onClick={() => setCurrentPage(article.page)}
                  className="text-left bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-green-200 transition-all"
                >
                  <span className="bg-green-100 text-green-900 text-xs font-medium px-2 py-0.5 rounded-full">{article.category}</span>
                  <p className="font-bold text-gray-800 text-sm mt-2 mb-1">{article.shortTitle}</p>
                  <p className="text-gray-500 text-xs leading-snug line-clamp-2">{article.title}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* この記事を書いた人 */}
        <section className="bg-amber-50 rounded-xl p-5 flex items-start gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden border border-amber-100 shadow-sm flex-shrink-0">
            <img src="/images/profile.png" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="text-sm">
            <p className="text-xs text-gray-500 mb-0.5">この記事を書いた人</p>
            <p className="font-bold text-gray-800">臨床ナースラボ 管理者<span className="ml-2 text-green-800 font-medium text-xs">現役看護師</span></p>
            <p className="text-gray-600 text-xs leading-relaxed mt-1.5">
              日々の臨床で感じた「これ、もっと早く知りたかった」をもとに、チームで判断を揃えるための知識とツールを作っています。
            </p>
            <button onClick={() => setCurrentPage('profile')} className="mt-2 text-green-800 text-xs font-medium hover:underline flex items-center gap-1">
              プロフィールを見る <ArrowRight size={12} />
            </button>
          </div>
        </section>

        {/* 一覧へ */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setCurrentPage('articles')}
            className="flex-1 bg-white text-green-800 px-5 py-3 rounded-lg font-medium border border-green-300 hover:bg-amber-50 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            記事一覧を見る <ArrowRight size={16} />
          </button>
          <button
            onClick={() => setCurrentPage('tools')}
            className="flex-1 bg-green-800 text-white px-5 py-3 rounded-lg font-medium hover:bg-green-900 transition-colors flex items-center justify-center gap-2 text-sm"
          >
            ツール一覧を見る <ArrowRight size={16} />
          </button>
        </div>

      </div>
    </div>
  );
}
